// Buyer-facing escalation route for a refund that the seller declined, or that
// the seller's policy never covered.
//
// This exists because the Terms make a promise the UI has to keep. The refund
// clause says every buyer always has a path to a refund: the seller's policy is
// the intended path, and escalating to supa.place support is the path that always
// exists. Before this, that promise was kept only in an email footer and in one
// Help-page FAQ answer, so a buyer who checked the app and saw "Refund Denied"
// had nothing to click. A buyer with no visible route goes to their card issuer
// instead, which costs a dispute fee and a scheme-ratio hit on a case we would
// frequently have refunded.
//
// The Help FAQ asks the buyer to quote three things (product id, order id, and
// the date they requested it). Making a person dig those out of a receipt is
// exactly the friction that sends them to their bank, so the link pre-fills them.

// Coded outcomes for a PLATFORM refund decision, offered beside the free-text
// reviewer note in the dev console.
//
// MIRRORS REFUND_REASON_CODES in functions/src/index.ts — keep in sync. The
// server validates against its own copy and rejects anything it does not
// recognise, so a drift here surfaces as a rejected decision rather than a
// mis-stored one.
//
// The note is what the buyer reads. The code is what makes the decisions
// queryable later: how many declines were change-of-mind after a full download,
// whether one seller accounts for most "not as described" approvals, whether
// goodwill refunds are drifting up. Free text answers none of that.
export const REFUND_REASON_CODES: ReadonlyArray<{
    code: string; label: string; appliesTo: 'Accepted' | 'Denied' | 'both';
}> = [
    { code: 'faulty_or_corrupt', label: 'Files faulty, corrupt or unopenable', appliesTo: 'Accepted' },
    { code: 'not_as_described', label: 'Not as described on the listing', appliesTo: 'Accepted' },
    { code: 'not_delivered', label: 'Paid but never delivered', appliesTo: 'Accepted' },
    { code: 'duplicate_purchase', label: 'Duplicate purchase', appliesTo: 'Accepted' },
    { code: 'seller_agreed', label: 'Seller agreed but did not action it', appliesTo: 'Accepted' },
    { code: 'statutory_right', label: 'Statutory consumer guarantee / withdrawal right', appliesTo: 'Accepted' },
    { code: 'goodwill', label: 'Goodwill (not owed, granted anyway)', appliesTo: 'Accepted' },
    { code: 'as_described', label: 'Product matches the listing', appliesTo: 'Denied' },
    { code: 'change_of_mind_out_of_window', label: 'Change of mind, outside the seller’s window', appliesTo: 'Denied' },
    { code: 'downloaded_in_full', label: 'Downloaded in full, no fault shown', appliesTo: 'Denied' },
    { code: 'abuse_pattern', label: 'Refund abuse pattern', appliesTo: 'Denied' },
    { code: 'insufficient_evidence', label: 'Claim unsupported after review', appliesTo: 'Denied' },
    { code: 'other', label: 'Other (explain in the note)', appliesTo: 'both' },
];

/** Codes offerable for one decision. Prevents self-contradicting selections. */
export function reasonCodesFor(decision: 'Accepted' | 'Denied') {
    return REFUND_REASON_CODES.filter((c) => c.appliesTo === decision || c.appliesTo === 'both');
}

/** Fields the support form needs to identify a refund without the buyer hunting. */
export interface RefundAppealContext {
  productId: string;
  productName?: string;
  /** The order / payment intent reference printed on the receipt. */
  orderId?: string;
  /** When the buyer originally requested the refund, epoch millis. */
  requestedAtMs?: number | null;
}

/**
 * Why the buyer is here, which changes the opening line of the pre-filled message.
 *
 * 'decision' is the original case: the seller declined a request the buyer made,
 * and the buyer is contesting that decision.
 *
 * 'no-policy' is the seller having switched self-service refunds off entirely, so
 * there is no request and no decision to contest. Opening with "review a refund
 * decision" there reads as nonsense to the buyer and, worse, sends support
 * looking for a refund document that does not exist.
 */
export type RefundAppealReason = 'decision' | 'no-policy';

/**
 * Whether a denied refund may still be appealed.
 *
 * A denial by the SELLER can be escalated to supa.place, because we are merchant
 * of record and the decision is ultimately ours to make. A denial by supa.place
 * cannot be escalated to supa.place: that is the end of the internal process, and
 * the refund-result email says as much ("This decision is final"). Offering an
 * appeal button there would send the buyer round a loop that cannot resolve.
 *
 * `resolvedByAdmin` is set on the refund document when ops decides a request.
 *
 * @param {object} refund The refund record's status and resolver.
 * @return {boolean} True when an appeal route should be offered.
 */
export function canAppealRefund(
  refund: { status?: string | null; resolvedByAdmin?: boolean | null } | null | undefined,
): boolean {
  if (!refund) return false;
  if (refund.resolvedByAdmin === true) return false;
  return refund.status === 'Denied';
}

/**
 * A Help-page link that lands on the support form with the refund already
 * described, so the buyer does not have to assemble the details the FAQ asks for.
 *
 * Deliberately a plain link to our own Help page rather than a `mailto:`. A
 * mailto depends on a configured mail client, silently does nothing on many
 * phones and most webmail-only setups, and loses the buyer entirely at the exact
 * moment they are already frustrated.
 *
 * @param {RefundAppealContext} ctx What is known about the purchase.
 * @param {RefundAppealReason} reason Which situation brought the buyer here.
 * @return {string} Relative URL to the Help page contact form.
 */
export function refundAppealHref(
  ctx: RefundAppealContext,
  reason: RefundAppealReason = 'decision',
): string {
  const lines = [
    reason === 'no-policy' ?
      'This product does not offer self-service refunds, and I would like ' +
        'supa.place to review my purchase.' :
      'I would like supa.place to review a refund decision.',
    '',
    ctx.productName ? `Product: ${ctx.productName}` : '',
    `Product ID: ${ctx.productId}`,
    ctx.orderId ? `Order ID: ${ctx.orderId}` : '',
    ctx.requestedAtMs ?
      `Refund requested: ${new Date(ctx.requestedAtMs).toISOString().slice(0, 10)}` : '',
    '',
    'Why I think this should be refunded:',
    '',
  ].filter((l) => l !== null && l !== undefined);
  const params = new URLSearchParams({
    topic: 'refund-appeal',
    message: lines.join('\n'),
  });
  return `/help?${params.toString()}#contact`;
}
